import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _uniqId from 'lodash/uniqueId';
import { Table, Popover, PopConfirm, Icon, Tooltip } from 'grab-ui';
import {
  TABLE_COLUMN_V2,
  TABLE_COLUMN_V2_MUST_SHOW,
  REVIEW_ORDER_FIELDS,
} from 'constants/bulk-upload-constants';
import { STORAGE_LOCALE } from 'constants/app-constants';
import { CASH_BY_RECIPIENT, CASH_BY_SENDER } from 'constants/booking-constants';
import { getAmountDisplay, formatMajorUnitsForDisplay } from 'helper/order';
import { getStopItineraryIndex, getRecipientStops } from 'helper/bulk-upload';
import { paymentMethodForDisplay } from 'helper/payment';
import { withCashlessPaymentDisabledPopover } from 'components/hoc/with-cashless-disabled-popover';

import css from './bulk-review-table.less';

class BulkReviewTable extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  onClickEdit = (fieldKey, recordIndex, itineraryIndex) => e => {
    e.preventDefault();

    const { onEditErrorField } = this.props;
    onEditErrorField(recordIndex, fieldKey, itineraryIndex);
  };

  getReviewErrors = record => _get(record, 'errors', []);

  getQuoteErrors = record => _get(record, 'quote.errors', []);

  getBookingLimitErrors = record => _get(record, 'bookingLimit.errors', []);

  getTotalErrors = record =>
    [].concat(
      this.getReviewErrors(record),
      this.getQuoteErrors(record),
      this.getBookingLimitErrors(record),
    );

  // filter errors not displayed in review table columns
  getInvisibleErrors = record => {
    const invisibleReviewErrors = this.getReviewErrors(record).filter(
      e =>
        _get(e, 'type') &&
        !REVIEW_ORDER_FIELDS[_get(e, 'type')].visibleInReviewTableColumn,
    );
    return [].concat(this.getQuoteErrors(record), invisibleReviewErrors);
  };

  getPaymentMethod = order => {
    const { paidBy, payment } = order;
    if (paidBy) {
      if (paidBy === CASH_BY_RECIPIENT) {
        return I18n.t('booking.paymentMethod.cashByRecipient');
      }
      if (paidBy === CASH_BY_SENDER) {
        return I18n.t('booking.paymentMethod.cashBySender');
      }
      return I18n.t('booking.paymentMethod.cash');
    }
    return paymentMethodForDisplay(payment, true);
  };

  getFeeAmount = record =>
    _get(record, 'quote.amount') && !_isEmpty(_get(record, 'quote.currency'))
      ? getAmountDisplay(
          _get(record, 'quote.amount'),
          _get(record, 'quote.currency'),
          false,
        )
      : 0;

  getPromo = (promoCode, isPromoApplied) => {
    if (!promoCode) return null;

    const promoStatus = isPromoApplied ? (
      <Icon
        type="check-circle"
        theme="twoTone"
        twoToneColor="#00b14f"
        className={css.promoStatusIcon}
      />
    ) : (
      <Icon
        type="close-circle"
        theme="twoTone"
        twoToneColor="#ee6352"
        className={css.promoStatusIcon}
      />
    );

    return (
      <div className={css.promo}>
        <img
          className={css.paymentIcon}
          src={require('public/images/offer.svg')}
          alt=""
        />
        <span className={css.promoContent}>{promoCode}</span>
        {promoStatus}
      </div>
    );
  };

  getFieldDisplayValue = value => {
    if (!_isEmpty(value)) return value;

    return I18n.t('v2.error.uploadOrder.validationError.empty_field');
  };

  generateReviewScreen = () => {
    const { reviewItems, visibleColumns } = this.props;
    const COLUMNS = [
      {
        title: (
          <span>{I18n.t('v2.homepage.uploadOrder.reviewTable.column.no')}</span>
        ),
        key: TABLE_COLUMN_V2.NO.key,
        width: 50,
        render: (_, record, index) => (
          <div>
            <h5 className={css.text}>{index + 1}</h5>
          </div>
        ),
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.batch')}
          </span>
        ),
        key: TABLE_COLUMN_V2.REF.key,
        width: 70,
        render: (_, record, index) => {
          const errors = this.getReviewErrors(record);

          return getRecipientStops(record).map((stop, _stopIndex) => {
            const { isMultipleStop, batchReference, sequenceNumber } = stop;
            const content = isMultipleStop ? (
              <div>
                <h5 className={css.text}>
                  {batchReference} - {sequenceNumber}
                </h5>
              </div>
            ) : (
              <h5 className={css.text}>-</h5>
            );
            return (
              <div key={_uniqId('batchRef_')} className={css.columnFragment}>
                {this.renderWithErrorPopover(
                  index,
                  errors,
                  REVIEW_ORDER_FIELDS.SEQUENCE_NUMBER.key,
                  content,
                  getStopItineraryIndex(stop),
                )}
              </div>
            );
          });
        },
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.sender')}
          </span>
        ),
        key: TABLE_COLUMN_V2.SENDER_DETAILS.key,
        width: 250,
        render: (_, record, index) => (
          <div className={css.contactSection}>
            {this.renderSender(record, index)}
          </div>
        ),
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.recipient')}
          </span>
        ),
        key: TABLE_COLUMN_V2.RECIPIENT_DETAILS.key,
        width: 250,
        render: (_, record, index) => (
          <div className={css.contactSection}>
            {this.renderReceiver(record, index)}
          </div>
        ),
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.deliveryType')}
          </span>
        ),
        key: TABLE_COLUMN_V2.SERVICE_TYPE.key,
        render: (_, record, index) => {
          const errors = this.getReviewErrors(record);

          return getRecipientStops(record).map((stop, _stopIndex) => (
            <div key={_uniqId('serviceType_')} className={css.columnFragment}>
              {this.renderWithErrorPopover(
                index,
                errors,
                REVIEW_ORDER_FIELDS.SERVICE_TYPE.key,
                <h5 className={css.text}>
                  {this.getFieldDisplayValue(_get(stop, 'serviceType'))}
                </h5>,
                getStopItineraryIndex(stop),
              )}
            </div>
          ));
        },
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.cod')}
          </span>
        ),
        key: TABLE_COLUMN_V2.CASH_ON_DELIVERY.key,
        render: (_, record, index) => {
          const { quote } = record;
          const currency = _get(quote, 'currency');
          const errors = this.getReviewErrors(record);

          return getRecipientStops(record).map((stop, _stopIndex) => {
            let amount = _get(
              stop,
              REVIEW_ORDER_FIELDS.CASH_ON_DELIVERY.orderAttribute,
              '',
            );
            // display converted amount when having valid amount and currency
            // otherwise display amount (invalid) as it is
            if (
              !_isEmpty(amount) &&
              !isNaN(amount) &&
              amount > 0 &&
              !_isEmpty(currency)
            ) {
              amount = formatMajorUnitsForDisplay(amount, currency, false);
            }
            return (
              <div key={_uniqId('codAmount_')} className={css.columnFragment}>
                {this.renderWithErrorPopover(
                  index,
                  errors,
                  REVIEW_ORDER_FIELDS.CASH_ON_DELIVERY.key,
                  <h5 className={css.text}>{amount}</h5>,
                  getStopItineraryIndex(stop),
                )}
              </div>
            );
          });
        },
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.weight')}
          </span>
        ),
        key: TABLE_COLUMN_V2.WEIGHT.key,
        width: 70,
        render: (_, record, index) => {
          const errors = this.getReviewErrors(record);

          return getRecipientStops(record).map((stop, _stopIndex) => {
            let weightDisplay = _get(
              stop,
              REVIEW_ORDER_FIELDS.WEIGHT.orderAttribute,
              I18n.t('v2.error.uploadOrder.validationError.empty_field'),
            );
            if (
              weightDisplay !==
              I18n.t('v2.error.uploadOrder.validationError.empty_field')
            ) {
              weightDisplay = isNaN(weightDisplay)
                ? weightDisplay
                : `${weightDisplay} kg`;
            }
            return (
              <div key={_uniqId('weight_')} className={css.columnFragment}>
                {this.renderWithErrorPopover(
                  index,
                  errors,
                  REVIEW_ORDER_FIELDS.WEIGHT.key,
                  <h5 className={css.text}>{weightDisplay}</h5>,
                  getStopItineraryIndex(stop),
                )}
              </div>
            );
          });
        },
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.payment')}
          </span>
        ),
        key: TABLE_COLUMN_V2.PAYMENT.key,
        width: 150,
        render: (_, record, index) => this.renderPayment(record, index),
      },
      {
        title: (
          <span>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.column.status')}
          </span>
        ),
        key: TABLE_COLUMN_V2.STATUS.key,
        width: 100,
        render: (_, record, index) => (
          <div className={css.status}>{this.renderStatus(record, index)}</div>
        ),
      },
    ];

    const showColumns = COLUMNS.filter(
      col =>
        visibleColumns.includes(col.key) ||
        TABLE_COLUMN_V2_MUST_SHOW.includes(col.key),
    );

    return (
      <Table
        className={css.table}
        rowClassName={css.row}
        columns={showColumns}
        rowKey={record => _uniqId('row_')}
        pagination={false}
        dataSource={reviewItems}
        // scroll={{ y: innerHeight - 56 - 48 - 45 - 48 - 54 - 92 }}
      />
    );
  };

  renderErrorList = (errors, fieldKey, itineraryIndex) => {
    const relatedErrors = errors
      .filter(
        e =>
          _get(e, 'type') === fieldKey &&
          _get(e, 'itineraryIndex') === itineraryIndex,
      )
      .map(e => e.message);
    return relatedErrors.map(e => <h5 key={_uniqId('error_')}>{e}</h5>);
  };

  errorCodeToMsg = e => {
    const locale = window.localStorage.getItem(STORAGE_LOCALE);
    if (e.errorCode === 1000 && locale === 'vi') {
      return I18n.t('v2.error.uploadOrder.validationError.too_far');
    }
    return e.message;
  };

  // errors not highlighted in table columns will be list in right end error popover
  renderInvisibleErrorsPopover = (record, recordIndex) => {
    const invisibleErrors = this.getInvisibleErrors(record);
    if (_isEmpty(invisibleErrors)) return null;

    return (
      <Popover
        overlayClassName={css.errorPopoverContent}
        content={
          <div>
            {invisibleErrors.map(e => (
              <h5 key={_uniqId('invisibleError_')}>{this.errorCodeToMsg(e)}</h5>
            ))}
          </div>
        }
        placement="bottom"
        title={null}
        trigger="hover"
      >
        <div className={css.errorText}>
          <div className={css.errorCount}>
            <h5>
              {invisibleErrors.length > 1
                ? I18n.t('v2.error.uploadOrder.validationError.moreErrors', {
                    length: invisibleErrors.length,
                  })
                : I18n.t('v2.error.uploadOrder.validationError.moreError', {
                    length: invisibleErrors.length,
                  })}
            </h5>
          </div>
        </div>
      </Popover>
    );
  };

  renderWithErrorPopover = (
    recordIndex,
    errors,
    fieldKey,
    content,
    itineraryIndex,
  ) => {
    const errList = this.renderErrorList(errors, fieldKey, itineraryIndex);

    if (_isEmpty(errList)) return content;
    // show edit button for in-line editable errors only
    const errEditable = REVIEW_ORDER_FIELDS[fieldKey].editable;

    return (
      <Popover
        overlayClassName={css.errorPopoverContent}
        content={
          <div>
            {errList}
            {errEditable && (
              <a
                onClick={this.onClickEdit(
                  fieldKey,
                  recordIndex,
                  itineraryIndex,
                )}
                role="button"
                tabIndex={-1}
                onKeyPress={null}
              >
                {I18n.t('v2.homepage.uploadOrder.reviewTable.action.edit')}
              </a>
            )}
          </div>
        }
        placement="bottom"
        title={null}
        trigger="hover"
      >
        <div className={css.errorText}>{content}</div>
      </Popover>
    );
  };

  renderPayment = (record, index) => {
    const { onRetryFailedPayments } = this.props;
    const errors = this.getReviewErrors(record);

    const feeAmount = this.getFeeAmount(record);
    const { promo: promoCode } = record;
    const isDiscountApplied = _get(record, 'quote.isDiscountApplied', false);
    return getRecipientStops(record).map((stop, stopIndex) => {
      const paymentMethod = this.getPaymentMethod(stop);
      /* only display amount in first row to avoid
       * misunderstanding that each stop has a separate price
       */
      const content =
        stopIndex === 0 ? (
          <Fragment>
            <h5 className={css.paymentAmount}>{feeAmount}</h5>
            <h5 className={css.caption}>{paymentMethod}</h5>
            {this.getPromo(promoCode, isDiscountApplied)}
          </Fragment>
        ) : (
          <Fragment>
            <h5 className={css.caption}>{paymentMethod}</h5>
            {this.getPromo(promoCode, isDiscountApplied)}
          </Fragment>
        );
      const failedPaymentError = errors.find(
        e => e.errorKey === 'payment_method_disabled',
      );
      if (failedPaymentError) {
        const popoverOptions = {
          child: <div className={css.errorText}>{content}</div>,
          message: I18n.t(
            'v2.error.uploadOrder.reviewTable.cashPaymentDisabledText',
          ),
          buttonText: I18n.t(
            'v2.error.uploadOrder.reviewTable.cashPaymentDisabledButton',
          ),
          onRetry: onRetryFailedPayments,
          placement: 'bottom',
        };

        return (
          <div key={_uniqId('payment_')} className={css.columnFragment}>
            {withCashlessPaymentDisabledPopover(popoverOptions)}
          </div>
        );
      }
      return (
        <div key={_uniqId('payment_')} className={css.columnFragment}>
          {this.renderWithErrorPopover(
            index,
            errors,
            REVIEW_ORDER_FIELDS.PAYMENT.key,
            content,
            getStopItineraryIndex(stop),
          )}
        </div>
      );
    });
  };

  renderStatus = (record, index) => {
    const totalErrors = this.getTotalErrors(record);
    const totalErrorCount = totalErrors.length;
    const bookingLimitErrorMessage = _get(record, 'bookingLimit.errors[0]', '');
    const { onDeleteRecord } = this.props;
    return (
      <Fragment>
        <Tooltip placement="bottom" title={bookingLimitErrorMessage}>
          <img
            src={
              totalErrorCount === 0
                ? require('public/images/status-valid.svg')
                : require('public/images/status-invalid.svg')
            }
            alt="status"
          />
        </Tooltip>
        <PopConfirm
          cancelText={I18n.t(
            'v2.homepage.uploadOrder.reviewTable.deleteOrderConfirm.cancelText',
          )}
          okText={I18n.t(
            'v2.homepage.uploadOrder.reviewTable.deleteOrderConfirm.okText',
          )}
          onConfirm={onDeleteRecord(index)}
          placement="left"
          title={I18n.t(
            'v2.homepage.uploadOrder.reviewTable.deleteOrderConfirm.title',
          )}
        >
          <img
            className={css.actionButton}
            src={require('public/images/delete.svg')}
            alt="delete"
            onKeyDown={null}
          />
        </PopConfirm>
        {this.renderInvisibleErrorsPopover(record, index)}
      </Fragment>
    );
  };

  renderAddressLabel = data => (
    <div>
      {_get(
        data,
        'keywords',
        I18n.t('v2.error.uploadOrder.validationError.empty_field'),
      )}{' '}
      •{' '}
      {_get(
        data,
        'address',
        I18n.t('v2.error.uploadOrder.validationError.empty_field'),
      )}
    </div>
  );

  renderSender = (record, orderIndex) => {
    const { onClickRepickLocation } = this.props;
    const errors = this.getReviewErrors(record);

    return getRecipientStops(record).map((stop, stopIndex) => {
      const itineraryIndex = getStopItineraryIndex(stop);
      const senderData = _get(stop, 'sender', {});
      const nameSection = this.renderWithErrorPopover(
        orderIndex,
        errors,
        REVIEW_ORDER_FIELDS.SENDER_NAME.key,
        <h5 className={css.text}>
          {this.getFieldDisplayValue(_get(stop, 'sender.name'))}
        </h5>,
        itineraryIndex,
      );
      const phoneSection = this.renderWithErrorPopover(
        orderIndex,
        errors,
        REVIEW_ORDER_FIELDS.SENDER_PHONE.key,
        <h5 className={css.caption}>
          {this.getFieldDisplayValue(_get(stop, 'sender.phone'))}
        </h5>,
        itineraryIndex,
      );
      const addressSectionErrList = this.renderErrorList(
        errors,
        REVIEW_ORDER_FIELDS.SENDER_ADDRESS.key,
        itineraryIndex,
      );
      return (
        <div key={_uniqId('sender_')} className={css.columnFragment}>
          {nameSection}
          {phoneSection}
          <Popover
            overlayClassName={css.addressInfoContent}
            content={
              <Fragment>
                {!_isEmpty(addressSectionErrList) && (
                  <div className={css.errorText}>{addressSectionErrList}</div>
                )}
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.originalAddress',
                    )}
                  </h5>
                  <span>
                    {_get(
                      senderData,
                      'input',
                      I18n.t(
                        'v2.error.uploadOrder.validationError.empty_field',
                      ),
                    )}
                  </span>
                </div>
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.adjustedTo',
                    )}
                  </h5>
                  <span>{this.renderAddressLabel(senderData)}</span>
                </div>
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.locationDetails',
                    )}
                  </h5>
                  <span>{_get(senderData, 'instruction', '')}</span>
                  <a
                    onClick={onClickRepickLocation({
                      order: record,
                      itineraryIndex: 0,
                      disabled: false,
                      originalAddressEntity: senderData,
                      isSender: true,
                    })}
                    role="button"
                    tabIndex={-1}
                    onKeyPress={null}
                  >
                    {I18n.t('v2.homepage.uploadOrder.reviewTable.action.edit')}
                  </a>
                </div>
              </Fragment>
            }
            placement="bottom"
            title={null}
            trigger="hover"
          >
            <h5
              className={
                _isEmpty(addressSectionErrList)
                  ? css.address
                  : css.addressWithErr
              }
            >
              {this.renderAddressLabel(senderData)}
            </h5>
          </Popover>
        </div>
      );
    });
  };

  renderReceiver = (record, orderIndex) => {
    const { onClickRepickLocation } = this.props;
    const recordErrors = _get(record, 'errors', []);
    const quoteErrors = _get(record, 'quote.errors', []).map(e => ({
      message: _get(e, 'message', 'Unknown quote error'),
      // display quote related error in receiver address field
      type: REVIEW_ORDER_FIELDS.RECEIVER_ADDRESS.key,
    }));
    const totalErrors = [...recordErrors, ...quoteErrors];

    return getRecipientStops(record).map((stop, stopIndex) => {
      const itineraryIndex = getStopItineraryIndex(stop);
      const nameSection = this.renderWithErrorPopover(
        orderIndex,
        totalErrors,
        REVIEW_ORDER_FIELDS.RECEIVER_NAME.key,
        <h5 className={css.text}>
          {this.getFieldDisplayValue(_get(stop, 'name'))}
        </h5>,
        itineraryIndex,
      );

      const phoneSection = this.renderWithErrorPopover(
        orderIndex,
        totalErrors,
        REVIEW_ORDER_FIELDS.RECEIVER_PHONE.key,
        <h5 className={css.caption}>
          {this.getFieldDisplayValue(_get(stop, 'phone'))}
        </h5>,
        itineraryIndex,
      );
      const addressSectionErrList = this.renderErrorList(
        totalErrors,
        REVIEW_ORDER_FIELDS.RECEIVER_ADDRESS.key,
        itineraryIndex,
      );

      return (
        <div key={_uniqId('receiver_')} className={css.columnFragment}>
          {nameSection}
          {phoneSection}
          <Popover
            overlayClassName={css.addressInfoContent}
            content={
              <Fragment>
                {!_isEmpty(addressSectionErrList) && (
                  <div className={css.errorText}>{addressSectionErrList}</div>
                )}
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.originalAddress',
                    )}
                  </h5>
                  <span>
                    {_get(
                      stop,
                      'input',
                      I18n.t(
                        'v2.error.uploadOrder.validationError.empty_field',
                      ),
                    )}
                  </span>
                </div>
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.adjustedTo',
                    )}
                  </h5>
                  <span>{this.renderAddressLabel(stop)}</span>
                </div>
                <div>
                  <h5>
                    {I18n.t(
                      'v2.homepage.uploadOrder.reviewTable.action.locationDetails',
                    )}
                  </h5>
                  <span>{_get(stop, 'instruction', '')}</span>
                  <a
                    onClick={onClickRepickLocation({
                      order: record,
                      itineraryIndex,
                      disabled: false,
                      originalAddressEntity: stop,
                      isSender: false,
                    })}
                    role="button"
                    tabIndex={-1}
                    onKeyPress={null}
                  >
                    {I18n.t('v2.homepage.uploadOrder.reviewTable.action.edit')}
                  </a>
                </div>
              </Fragment>
            }
            placement="bottom"
            title={null}
            trigger="hover"
          >
            <h5
              className={
                _isEmpty(addressSectionErrList)
                  ? css.address
                  : css.addressWithErr
              }
            >
              {this.renderAddressLabel(stop)}
            </h5>
          </Popover>
        </div>
      );
    });
  };

  render() {
    return <div>{this.generateReviewScreen()}</div>;
  }
}

BulkReviewTable.propTypes = {
  reviewItems: PropTypes.arrayOf(PropTypes.shape({})),
  onClickRepickLocation: PropTypes.func.isRequired,
  onDeleteRecord: PropTypes.func.isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onEditErrorField: PropTypes.func.isRequired,
  onRetryFailedPayments: PropTypes.func.isRequired,
};

BulkReviewTable.defaultProps = {
  reviewItems: [],
  onClickRepickLocation: null,
};

export default BulkReviewTable;
